import React, { useState, useEffect, useMemo } from "react";
import { Form, Space, Spin } from "antd";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import { UserOutlined, SafetyOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useTheme } from "../../context/ThemeContext";
import {
  StyledModal,
  UserInfo,
  ModalFooter,
  CloseButton,
  SubmitButton,
  ModalContentWrapper,
  InfoAlert,
  UserAvatar,
  UserDetails,
} from "./Styles";
import { UserRoleModalProps } from "./types";
import CustomSelector from "../../commonComponents/Selector";

// All available roles
const ALL_ROLES = [
  { value: "merchant", label: "Merchant" },
  { value: "merchant_manager", label: "Manager" },
  { value: "merchant_staff", label: "Staff" },
];

const UserRoleModal: React.FC<UserRoleModalProps> = ({
  visible,
  onClose,
  user,
  onSave,
  isLoading = false,
  isSaving = false,
}) => {
  const { isDarkMode } = useTheme();
  const currentUserRole = useSelector((state: RootState) => state.auth.role);
  const [form] = Form.useForm();
  const [selectedRole, setSelectedRole] = useState<string>("");

  const availableRoles = useMemo(() => {
    if (currentUserRole === "admin") {
      return ALL_ROLES;
    } else if (currentUserRole === "merchant") {
      return ALL_ROLES.filter((role) => role.value !== "merchant");
    }
    return [];
  }, [currentUserRole]);

  useEffect(() => {
    if (visible && user) {
      // Use role string from API if available
      const userRole = user.role || "";
      setSelectedRole(userRole);
      form.setFieldsValue({
        role: userRole,
      });
    } else {
      setSelectedRole("");
      form.resetFields();
    }
  }, [user, visible, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const roleData = {
        role: values.role || "",
      };
      onSave(roleData);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  if (!user) return null;

  const getUserInitials = (name: string | undefined, username: string | undefined, email: string | undefined) => {
    if (name) {
      const parts = name.split(" ");
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name[0].toUpperCase();
    }
    if (username) {
      return username[0].toUpperCase();
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return "U";
  };

  const styledTheme: DefaultTheme = { mode: isDarkMode ? "dark" : "light" };

  return (
    <StyledThemeProvider theme={styledTheme}>
    <StyledModal
      title={
        <Space>
          <UserOutlined />
          <span>Update User Role</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={600}
      destroyOnClose
      zIndex={2000}
      footer={
        <ModalFooter>
          <CloseButton onClick={onClose} disabled={isSaving}>
            Cancel
          </CloseButton>
          <SubmitButton type="primary" onClick={handleSave} loading={isSaving}>
            Update Role
          </SubmitButton>
        </ModalFooter>
      }
    >
      <Spin spinning={isLoading}>
        <ModalContentWrapper>
          <UserInfo>
            <UserAvatar size={56}>
              {getUserInitials(user.name, user.username, user.email)}
            </UserAvatar>
            <UserDetails>
              <div className="user-name">
                {user.name || user.username || user.email}
              </div>
              {user.email && <div className="user-email">{user.email}</div>}
            </UserDetails>
          </UserInfo>

          <Form form={form} layout="vertical">
            <Form.Item
              label={
                <Space>
                  <SafetyOutlined />
                  <span>* Assign Role</span>
                </Space>
              }
              name="role"
              rules={[
                {
                  required: true,
                  message: "Please select a role",
                },
              ]}
            >
              <CustomSelector
                placeholder="Select role for this user"
                value={selectedRole}
                onChange={(value: string | number) => {
                  setSelectedRole(value as string);
                }}
                options={availableRoles}
                width="100%"
                height={38}
                borderRadius={8}
              />
            </Form.Item>

            {selectedRole && (
              <InfoAlert
                message="Selected Role"
                description={
                  <Space>
                    <span>{availableRoles.find((r) => r.value === selectedRole)?.label || selectedRole}</span>
                  </Space>
                }
                type="info"
                showIcon
              />
            )}
          </Form>
        </ModalContentWrapper>
      </Spin>
    </StyledModal>
    </StyledThemeProvider>
  );
};

export default UserRoleModal;

